import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Settings } from 'lucide-react';
import { useChessTimer } from '../../context/ChessTimerContext';
import { ChessTimerSettings } from './ChessTimerSettings';

function formatChessTime(seconds: number): string {
  const validSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(validSeconds / 60);
  const remainingSeconds = validSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function ChessTimer() {
  const { state, startTimer, pauseTimer, resetTimer, flipTimer } = useChessTimer();
  const [isStarting, setIsStarting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [animateReset, setAnimateReset] = useState(false);

  const handleStartClick = useCallback(async () => {
    setIsStarting(true);
    startTimer();
    setIsStarting(false);
  }, [startTimer]);

  const handlePauseClick = useCallback(() => {
    pauseTimer();
    setIsStarting(false);
  }, [pauseTimer]);

  const handleResetClick = useCallback(() => {
    setAnimateReset(true);
    resetTimer();
    setTimeout(() => setAnimateReset(false), 300);
  }, [resetTimer]);

  const handleFlipClick = useCallback(() => {
    flipTimer();
  }, [flipTimer]);

  // Click on timer to select that player
  const handleTimerClick = useCallback((player: 'white' | 'black') => {
    if (state.activePlayer !== player) {
      flipTimer();
    }
  }, [state.activePlayer, flipTimer]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }

      const target = event.target as HTMLElement;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      switch (event.code) {
        case 'Space':
          event.preventDefault();
          if (state.isRunning && isStarting) return;
          if (state.isRunning) handlePauseClick();
          else handleStartClick();
          break;
        case 'Backspace':
        case 'KeyR':
          event.preventDefault();
          handleResetClick();
          break;
        case 'KeyF':
          event.preventDefault();
          handleFlipClick();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isStarting, handleStartClick, handlePauseClick, handleResetClick, handleFlipClick, state.isRunning]);

  const whiteTime = state.whiteElapsed;
  const blackTime = state.blackElapsed;

  return (
    <div className="chess-timer flex flex-col items-center justify-center w-full max-w-7xl mx-auto px-4 py-8">
      {/* Classic table-top chess timer container - wide format */}
      <div className="chess-timer-body relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-3xl p-6 md:p-10 shadow-2xl border-4 border-gray-700 dark:border-gray-600">
        
        {/* LEAP-style rocker paddle: one bar that rocks about its center and straddles the top edge of the case, sitting over it.
            Active player's end is up, the other end is down. */}
        <button
          onClick={handleFlipClick}
          className="chess-paddle absolute left-4 right-4 md:left-8 md:right-8"
          aria-label="Flip paddle (F)"
          title="Flip paddle (F)"
        >
          <span className={`chess-paddle-bar ${state.activePlayer === 'white' ? 'is-white-up' : 'is-black-up'}`}>
            <span className="chess-paddle-shape" />
          </span>
        </button>
        <div className="h-6 md:h-8" aria-hidden="true" />

        {/* Main timer displays - side by side, wide format */}
        <div className="chess-displays flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 mb-8">
          
          {/* White timer - LEFT SIDE (clickable to select) */}
          <button
            onClick={() => handleTimerClick('white')}
            className={`chess-timer-display flex flex-col items-center justify-center p-6 md:p-10 rounded-2xl transition-all duration-500 ${
              state.activePlayer === 'white' 
                ? 'is-selected bg-white dark:bg-gray-800 shadow-2xl ring-4 ring-indigo-500 dark:ring-indigo-400 cursor-default' 
                : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer'
            }`}
            aria-label="Select White timer"
            title="Select White (click)"
          >
            <div className={`text-6xl md:text-8xl lg:text-9xl font-mono font-bold tabular-nums tracking-wider text-gray-900 dark:text-white transition-all duration-300 ${
              state.activePlayer === 'white' ? 'scale-110' : 'scale-100'
            }`}>
              {formatChessTime(whiteTime)}
            </div>
          </button>

          {/* Black timer - RIGHT SIDE (clickable to select) */}
          <button
            onClick={() => handleTimerClick('black')}
            className={`chess-timer-display flex flex-col items-center justify-center p-6 md:p-10 rounded-2xl transition-all duration-500 ${
              state.activePlayer === 'black' 
                ? 'is-selected bg-black dark:bg-gray-950 shadow-2xl ring-4 ring-indigo-500 dark:ring-indigo-400 cursor-default' 
                : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 cursor-pointer'
            }`}
            aria-label="Select Black timer"
            title="Select Black (click)"
          >
            <div className={`text-6xl md:text-8xl lg:text-9xl font-mono font-bold tabular-nums tracking-wider text-white dark:text-gray-100 transition-all duration-300 ${
              state.activePlayer === 'black' ? 'scale-110' : 'scale-100'
            }`}>
              {formatChessTime(blackTime)}
            </div>
          </button>

        </div>

        {/* Bottom control panel - Start/Pause in the middle */}
        <div className="chess-controls flex flex-wrap items-center justify-center gap-4">
          {/* Settings Button */}
          <button
            onClick={() => setShowSettings(true)}
            className="p-4 rounded-full hover:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300 hover:scale-110 active:scale-95 text-gray-200"
            aria-label="Timer settings"
            title="Settings"
          >
            <Settings className="w-7 h-7" />
          </button>

          {/* Start/Pause Button - CENTER */}
          <button
            onClick={state.isRunning ? handlePauseClick : handleStartClick}
            disabled={!state.isRunning && isStarting}
            className={`p-5 md:p-6 rounded-full text-white transition-all duration-300 shadow-xl ${
              state.isRunning 
                ? 'bg-red-600 hover:bg-red-500' 
                : 'bg-green-600 hover:bg-green-500'
            } ${
              !state.isRunning && isStarting ? 'opacity-75 cursor-not-allowed' : 'hover:scale-105'
            } ${
              state.isRunning ? 'animate-pulse' : ''
            }`}
            aria-label={state.isRunning ? "Pause timer" : "Start timer"}
            title={state.isRunning ? "Pause (Space)" : "Start (Space)"}
          >
            {state.isRunning ? (
              <Pause className="w-10 h-10 md:w-12 md:h-12" />
            ) : (
              <Play className="w-10 h-10 md:w-12 md:h-12" />
            )}
          </button>

          {/* Reset Button */}
          <button
            onClick={handleResetClick}
            className={`p-4 rounded-full hover:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300 hover:scale-110 active:scale-95 text-gray-200 ${
              animateReset ? 'animate-spin' : ''
            }`}
            aria-label="Reset timers"
            title="Reset timers (R)"
          >
            <RotateCcw className="w-7 h-7" />
          </button>
        </div>
      </div>

      {/* Chess Timer Settings Modal */}
      {showSettings && (
        <ChessTimerSettings onClose={() => setShowSettings(false)} />
      )}

      {/* Keyboard shortcuts hint */}
      <p className="chess-shortcuts mt-6 text-xs text-gray-400 dark:text-gray-500 select-none">
        Space: Start/Pause &middot; F: Flip &middot; R: Reset
      </p>
    </div>
  );
}
